import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { DEFAULT_LOCALE, LOCALES, translate } from './dictionary';

const STORAGE_KEY = 'eventhub.locale';

const LocaleContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  toggleLocale: () => {},
  t: (key) => key
});

function readStoredLocale() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LOCALES.includes(stored)) {
      return stored;
    }
  } catch {
    // Private mode or blocked storage - fall through to the default.
  }
  return DEFAULT_LOCALE;
}

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(readStoredLocale);

  const setLocale = useCallback((next) => {
    if (!LOCALES.includes(next)) {
      return;
    }
    setLocaleState(next);
    document.documentElement.lang = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persisting the choice is a convenience, not a requirement.
    }
  }, []);

  const value = useMemo(() => ({
    locale,
    setLocale,
    toggleLocale: () => setLocale(locale === 'hu' ? 'en' : 'hu'),
    t: (key, values) => translate(locale, key, values)
  }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}
